import React, { useState } from 'react';


const StaffEvaluationModal = ({ isOpen, onClose, staffId, staffName, onTriggerEvaluation }) => {
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await onTriggerEvaluation(staffId, staffName, startDate, endDate);
      
      // Handle async vs sync responses
      if (result && result.isAsync) {
        // Async processing
        setSuccess(`Evaluation request submitted successfully! Your evaluation is being processed asynchronously. Refresh the page in a few moments to see the results.`);
      } else if (result && result.request_id) {
        // Sync processing with request_id
        setSuccess(`Evaluation completed successfully! Request ID: ${result.request_id}`);
      } else if (result && result.message) {
        // Handle success message without request_id
        setSuccess(result.message || 'Evaluation request submitted successfully.');
      } else {
        setSuccess('Evaluation request submitted successfully.');
      }
      
      // Close modal after success (longer timeout for async messages)
      const timeout = result && result.isAsync ? 5000 : 3000; // 5s for async, 3s for sync
      setTimeout(() => {
        onClose();
        setSuccess(null);
      }, timeout);
    } catch (err) {
      setError(err.message || 'Failed to trigger evaluation');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Generate Staff Evaluation</h2>
        <p className="text-gray-600 mb-4">
          Generate a comprehensive performance evaluation for {staffName} based on call evaluations within the selected date range. 
          <br />
          <span className="text-sm text-blue-600 mt-1 block">
            Evaluations are processed asynchronously for faster response times.
          </span>
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 focus:outline-none"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting Request...' : 'Generate Evaluation'}
            </button>
          </div>
        </form>

        <div className="mt-4 text-sm text-gray-500">
          <p>Note: Evaluation generation may take a few minutes. The report will appear in the staff view once completed.</p>
        </div>
      </div>
    </div>
  );
};

export default StaffEvaluationModal;
